// src/components/DonationDialog.tsx
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Modal, Typography } from '@mui/material';
import React, { useState } from 'react';
import PaypalQR from '../assets/images/robert-garza-paypal-qr.png';
import VenmoQR from '../assets/images/robert-garza-venmo-qr.png';
import ZelleQR from '../assets/images/robert-garza-zelle-qr.png';
import { trackDonationClick } from './GoogleAnalytics';

interface DonationDialogProps {
  open?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
}

const DonationDialog: React.FC<DonationDialogProps> = ({ open: controlledOpen, onClose: controlledOnClose, onOpen: controlledOnOpen }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [venmoQROpen, setVenmoQROpen] = useState(false);
  const [paypalQROpen, setPaypalQROpen] = useState(false);
  const [zelleQROpen, setZelleQROpen] = useState(false);

  // Use controlled props if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleClose = controlledOnClose || (() => setInternalOpen(false));

  const handleClickOpen = () => {
    trackDonationClick();
    if (controlledOpen === undefined) {
      setInternalOpen(true);
    } else if (controlledOnOpen) {
      controlledOnOpen();
    }
  };

  const handleVenmoClick = () => {
    trackDonationClick('Venmo');
    setVenmoQROpen(true);
  };

  const handleVenmoQRClose = () => {
    setVenmoQROpen(false);
  };

  const handlePaypalClick = () => {
    trackDonationClick('PayPal');
    setPaypalQROpen(true);
  };

  const handlePaypalQRClose = () => {
    setPaypalQROpen(false);
  };

  const handleZelleClick = () => {
    trackDonationClick('Zelle');
    setZelleQROpen(true);
  };

  const handleZelleQRClose = () => {
    setZelleQROpen(false);
  };

  const handleCreditCardClick = () => {
    trackDonationClick('Credit Card');
    window.open('https://buy.stripe.com/5kQbJ2b9A8JVenFbfc3VC00', '_blank');
  };

  return (
    <div>
      <Button onClick={handleClickOpen}>
        <FavoriteIcon sx={{ mr: 1, color: 'red' }} /> Donate
      </Button>

      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Support Us</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your donations help cover the operational costs of this website, as well as marketing and expenses for
            advocacy efforts. Thank you for your support!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleCreditCardClick}
            sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
          >
            Credit Card
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleVenmoClick}
            sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
          >
            Venmo (@robertgarza)
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handlePaypalClick}
            sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
          >
            PayPal (robert@dima.ac)
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleZelleClick}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            Zelle
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={venmoQROpen}
        onClose={handleVenmoQRClose}
        aria-labelledby="venmo-qr-modal"
        aria-describedby="venmo-qr-code-image"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            outline: 'none',
            textAlign: 'center',
            width: { xs: '90vw', sm: '90vw', md: 'auto' },
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" id="venmo-qr-modal" sx={{ mb: 2 }}>
            Venmo QR Code
          </Typography>
          <img src={VenmoQR} alt="Venmo QR Code" style={{ maxWidth: '300px', width: '100%' }} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Scan to pay Robert Garza on Venmo
          </Typography>
          <Button onClick={handleVenmoQRClose} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>

      <Modal
        open={paypalQROpen}
        onClose={handlePaypalQRClose}
        aria-labelledby="paypal-qr-modal"
        aria-describedby="paypal-qr-code-image"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            outline: 'none',
            textAlign: 'center',
            width: { xs: '90vw', sm: '90vw', md: 'auto' },
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" id="paypal-qr-modal" sx={{ mb: 2 }}>
            PayPal QR Code
          </Typography>
          <img src={PaypalQR} alt="PayPal QR Code" style={{ maxWidth: '300px', width: '100%' }} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Scan with your phone camera to donate via PayPal
          </Typography>
          <Button onClick={handlePaypalQRClose} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>

      <Modal
        open={zelleQROpen}
        onClose={handleZelleQRClose}
        aria-labelledby="zelle-qr-modal"
        aria-describedby="zelle-qr-code-image"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            outline: 'none',
            textAlign: 'center',
            width: { xs: '90vw', sm: '90vw', md: 'auto' },
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" id="zelle-qr-modal" sx={{ mb: 2 }}>
            Zelle QR Code
          </Typography>
          <img src={ZelleQR} alt="Zelle QR Code" style={{ maxWidth: '300px', width: '100%' }} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Scan with your phone camera to donate via Zelle
          </Typography>
          <Button onClick={handleZelleQRClose} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default DonationDialog;
